import {ArrowDropDown as ArrowDropDownIcon, Close as CloseIcon} from '@mui/icons-material';
import {
  Button,
  Chip,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {FC, useCallback, useMemo, useState, useRef} from 'react';
import {styled} from 'styled-components';
import {useSearchParams, useLocation} from 'wouter';

import {FilterDpeMinMax} from '@website_frontend/components/filter_dpe_min_max';
import {FilterMinMax} from '@website_frontend/components/filter_min_max';
import {FilterTypeLabel} from '@website_frontend/components/filter_type_label';
import {filtersToUrlParams, useFilters} from '@website_frontend/lib/filters';
import {
  getCurrentSearch,
  getSearches,
  saveSearch,
  createNewSearch,
  setCurrentSearchId,
  type SavedSearch,
} from '@website_frontend/lib/user_data_storage';

type Props = {
  onApply?: () => void;
  filteredItemCount?: number;
};

export const FILTER_LABEL_WIDTH = 140;
export const FILTER_CONTROL_WIDTH = 220;

export const FilterForm: FC<Props> = ({onApply, filteredItemCount}) => {
  const [, setSearchParams] = useSearchParams();
  const [, setLocation] = useLocation();
  const [newSearchName, setNewSearchName] = useState('');
  const [showNewSearchDialog, setShowNewSearchDialog] = useState(false);
  const [searchMenuOpen, setSearchMenuOpen] = useState(false);
  const searchAnchorRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery('(max-width: 512px)');

  // Get current filter values from URL
  const {filters: currentFilters} = useFilters();

  const currentSearch = useMemo(() => getCurrentSearch(), []);
  const searches = useMemo(() => getSearches(), []);

  const handleSearchSelect = useCallback(
    (search: SavedSearch) => {
      setCurrentSearchId(search.id);
      setSearchMenuOpen(false);
      // Apply search filters to URL
      const params = filtersToUrlParams(search.filters);
      setLocation(`/?${params.toString()}`);
    },
    [setLocation]
  );

  // Check if filters have changed from saved search
  const hasChanges = useMemo(() => {
    if (!currentSearch) return true;
    const saved = currentSearch.filters;
    return (
      saved.priceMin !== currentFilters.priceMin ||
      saved.priceMax !== currentFilters.priceMax ||
      saved.surfaceMin !== currentFilters.surfaceMin ||
      saved.surfaceMax !== currentFilters.surfaceMax ||
      saved.bedroomsMin !== currentFilters.bedroomsMin ||
      saved.bedroomsMax !== currentFilters.bedroomsMax ||
      saved.roomsMin !== currentFilters.roomsMin ||
      saved.roomsMax !== currentFilters.roomsMax ||
      saved.landSurfaceMin !== currentFilters.landSurfaceMin ||
      saved.landSurfaceMax !== currentFilters.landSurfaceMax ||
      saved.floorNbMin !== currentFilters.floorNbMin ||
      saved.floorNbMax !== currentFilters.floorNbMax ||
      saved.energyCertificateMin !== currentFilters.energyCertificateMin ||
      saved.energyCertificateMax !== currentFilters.energyCertificateMax ||
      JSON.stringify(saved.typeLabel?.sort()) !==
        JSON.stringify(currentFilters.typeLabel?.sort()) ||
      saved.imagesMin !== currentFilters.imagesMin ||
      saved.imagesMax !== currentFilters.imagesMax
    );
  }, [currentSearch, currentFilters]);

  const reset = useCallback(() => {
    if (!currentSearch) {
      setSearchParams(new URLSearchParams(), {replace: true});
      return;
    }
    // Reset to current search's filters
    const params = filtersToUrlParams(currentSearch.filters);
    setSearchParams(params, {replace: true});
  }, [setSearchParams, currentSearch]);

  const handleApply = useCallback(() => {
    onApply?.();
  }, [onApply]);

  const handleSaveToCurrent = useCallback(() => {
    if (!currentSearch) return;
    const updated = {
      ...currentSearch,
      filters: currentFilters,
    };
    saveSearch(updated);
    onApply?.();
  }, [currentSearch, currentFilters, onApply]);

  const handleSaveAsNew = useCallback(() => {
    if (!newSearchName.trim()) return;
    createNewSearch(newSearchName.trim(), currentFilters);
    setNewSearchName('');
    setShowNewSearchDialog(false);
    onApply?.();
  }, [newSearchName, currentFilters, onApply]);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 0,
        display: 'flex',
        flexDirection: 'column',
        maxHeight: isMobile ? '100%' : '750px',
        overflow: 'auto',
      }}
    >
      <HeaderSection>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{px: 2, py: 1.5}}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography variant="h6">Filtres</Typography>
            {currentSearch && (
              <div ref={searchAnchorRef}>
                <Chip
                  label={currentSearch.name}
                  onClick={() => setSearchMenuOpen(true)}
                  icon={<ArrowDropDownIcon />}
                  variant="outlined"
                  size="small"
                />
              </div>
            )}
            <Menu
              open={searchMenuOpen}
              anchorEl={searchAnchorRef.current}
              onClose={() => setSearchMenuOpen(false)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
            >
              {searches.map(search => (
                <MenuItem
                  key={search.id}
                  onClick={() => handleSearchSelect(search)}
                  selected={search.id === currentSearch?.id}
                >
                  <ListItemText
                    primary={search.name}
                    secondary={search.isDefault ? 'Par défaut' : undefined}
                  />
                </MenuItem>
              ))}
            </Menu>
          </Stack>
          <IconButton size="small" onClick={onApply} aria-label="Fermer">
            <CloseIcon />
          </IconButton>
        </Stack>
      </HeaderSection>
      <ContentSection>
        <Stack spacing={1.5} useFlexGap sx={{px: 2, py: 2}}>
          <FilterTypeLabel title="Type de bien" paramName="typeLabel" />
          <FilterMinMax title="Prix (€)" minParamName="priceMin" maxParamName="priceMax" />
          <Divider sx={{my: 1.5}} />
          <FilterMinMax title="Surface (m²)" minParamName="surfaceMin" maxParamName="surfaceMax" />
          <FilterMinMax title="Étage" minParamName="floorNbMin" maxParamName="floorNbMax" />
          <FilterMinMax
            title="Surface terrain (m²)"
            minParamName="landSurfaceMin"
            maxParamName="landSurfaceMax"
          />
          <Divider sx={{my: 1.5}} />
          <FilterMinMax title="Nb chambres" minParamName="bedroomsMin" maxParamName="bedroomsMax" />
          <FilterMinMax title="Nombre de pièces" minParamName="roomsMin" maxParamName="roomsMax" />
          <Divider sx={{my: 1.5}} />
          <FilterDpeMinMax
            title="DPE"
            minParamName="energyCertificateMin"
            maxParamName="energyCertificateMax"
          />
          <FilterMinMax title="Nombre d'images" minParamName="imagesMin" maxParamName="imagesMax" />
        </Stack>
      </ContentSection>
      <FooterSection>
        <Stack spacing={1.5} useFlexGap sx={{px: 2, py: 2}}>
          <Stack direction="row" spacing={1} justifyContent="space-between">
            <Button variant="text" color="inherit" onClick={reset}>
              Réinitialiser
            </Button>
            <Button variant="contained" color="primary" onClick={handleApply}>
              {filteredItemCount !== undefined
                ? `Afficher ${filteredItemCount} annonces`
                : 'Appliquer'}
            </Button>
          </Stack>
          {hasChanges && (
            <Stack spacing={1}>
              {currentSearch && (
                <Button variant="outlined" color="primary" onClick={handleSaveToCurrent} fullWidth>
                  Enregistrer dans "{currentSearch.name}"
                </Button>
              )}
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => setShowNewSearchDialog(true)}
                fullWidth
              >
                Enregistrer comme nouvelle recherche
              </Button>
            </Stack>
          )}
          {showNewSearchDialog && (
            <Stack spacing={1}>
              <TextField
                label="Nom de la recherche"
                value={newSearchName}
                onChange={e => setNewSearchName(e.target.value)}
                size="small"
                autoFocus
                onKeyDown={e => {
                  if (e.key === 'Enter' && newSearchName.trim()) {
                    handleSaveAsNew();
                  }
                }}
              />
              <Stack direction="row" spacing={1}>
                <Button
                  variant="text"
                  onClick={() => {
                    setShowNewSearchDialog(false);
                    setNewSearchName('');
                  }}
                  fullWidth
                >
                  Annuler
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveAsNew}
                  disabled={!newSearchName.trim()}
                  fullWidth
                >
                  Enregistrer
                </Button>
              </Stack>
            </Stack>
          )}
        </Stack>
      </FooterSection>
    </Paper>
  );
};

FilterForm.displayName = 'FilterForm';

const HeaderSection = styled.div`
  background-color: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
  flex-shrink: 0;
`;

const ContentSection = styled.div`
  height: 0;
  flex-grow: 1;
  flex-basis: 1000px;
  overflow-y: auto;
  background-color: white;
`;

const FooterSection = styled.div`
  background-color: #f9f9f9;
  border-top: 1px solid #e0e0e0;
  flex-shrink: 0;
`;
